import { Fragment, useEffect, useRef } from "react";
import { Stage, Layer, Line } from "react-konva";
import type { Stage as StageLib } from "konva/lib/Stage";
import { twMerge } from "tailwind-merge";
import { Line as LineShape } from "konva/lib/shapes/Line";
import { Animation } from "konva/lib/Animation";

export function Canvas({
  polygons,
  stageSize,
  imageWidth,
  imageHeight,
}: {
  polygons?: [number, number][][];
  imageWidth?: number;
  imageHeight?: number;
  stageSize: { width: number; height: number };
}) {
  const stage = useRef<StageLib | null>(null);

  return (
    <Stage
      tabIndex={0}
      ref={stage}
      className={twMerge(["stage outline-none absolute top-0 left-0 z-10 [&>.konvajs-content]:mx-auto"])}
      width={stageSize.width}
      height={stageSize.height}
      onContextMenu={(e) => {
        // disable context menu
        e.evt.preventDefault();
        return false;
      }}
    >
      <Layer>
        {polygons &&
          polygons
            .filter(Boolean)
            .map((polygon, index) => (
              <Polygon
                key={index}
                polygon={polygon}
                stageSize={stageSize}
                imageWidth={imageWidth}
                imageHeight={imageHeight}
              />
            ))}
      </Layer>
    </Stage>
  );
}

function Polygon({
  polygon,
  imageHeight,
  imageWidth,
  stageSize,
}: {
  polygon: [number, number][];
  imageWidth?: number;
  imageHeight?: number;
  stageSize: { width: number; height: number };
}) {
  const downscalePoints = (
    points: [number, number][],
    { width, videoWidth, videoHeight }: { width: number; videoWidth: number; videoHeight: number },
    aspectRatio = 16 / 9
  ) =>
    points.flatMap((point) => [
      Math.floor((width * point[0]) / videoWidth),
      Math.floor(((width / aspectRatio) * point[1]) / videoHeight),
    ]);

  const lineRef = useRef<LineShape | null>(null);

  useEffect(() => {
    const anim = new Animation(function (frame) {
      if (!lineRef.current) return;
      if (!frame) return;

      const scale = Math.sin((frame.time * 2 * Math.PI) / 2000);
      lineRef.current.fill(`rgba(255,0,0, ${scale - 0.5})`);
    });

    anim.start();
  }, []);

  return (
    <Fragment>
      <Line
        globalCompositeOperation="destination-out"
        points={downscalePoints(polygon, {
          width: stageSize.width,
          videoWidth: imageWidth || 0,
          videoHeight: imageHeight || 0,
        })}
        fill="black"
        listening={false}
        closed
      />
      <Line
        ref={lineRef}
        points={downscalePoints(polygon, {
          width: stageSize.width,
          videoWidth: imageWidth || 0,
          videoHeight: imageHeight || 0,
        })}
        fill="rgba(0,0,0, 0.1)"
        stroke={"red"}
        strokeWidth={2}
        strokeEnabled
        closed={true}
      />
    </Fragment>
  );
}
